import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import Icon from '@mui/material/Icon';
import {
  Alert,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import { useTranslation } from 'react-i18next';
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import CashInfo from './CashInfo';
import PosPrintSurvey from './PosPrintSurvey';

function initStepper({ icon, completed }) {
  const icons_complete = {
    1: <Icon color='info'>how_to_reg</Icon>,
    2: <Icon color='info'>money</Icon>,
    3: <Icon color='info'>task_alt</Icon>,
  };
  const icons = {
    1: <Icon color='disabled'>how_to_reg</Icon>,
    2: <Icon color='disabled'>money</Icon>,
    3: <Icon color='disabled'>task_alt</Icon>,
  };
  return <div>{completed ? icons_complete[String(icon)] : icons[String(icon)]}</div>;
}

const stepperName = {
  authen: {
    name: 'ยืนยันตัวตน Audit',
  },
  counting: {
    name: 'นับเงิน',
  },
  success: {
    name: 'เสร็จสิ้น',
  },
};

const initAttr = {
  username: '',
  password: '',
};

export default function PosSurvey({ setBlockScan, sizeButton, buttonLoading, setButtonLoading }) {
  const mod = 'pos_survey';
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [modalAdd, setModalAdd] = useState(false);
  const btnRef = useRef(null);
  const { t, i18n } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [verify, setVerify] = useState(false);
  const app = useSelector((state) => state[APP_STATE]);
  const [notify, setNotify] = useState(null);
  const [cboCash, setCboCash] = useState([]);
  const [total, setTotal] = useState('');
  const [list, setList] = useState([]);
  const [audit, setAudit] = useState(0);
  const printRef = useRef(null);
  const [drop, setDrop] = useState(0);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const checkVerify = () => {
    if (!Func.verifyLength(attr.username, 5)) {
      setVerify({ ...verify, username: true });
      return false;
    }

    if (!Func.verifyLength(attr.password, 5)) {
      setVerify({ username: false, password: true });
      return false;
    }

    setVerify(false);
    return true;
  };

  const init = () => {
    setLoading(true);

    FetchData('GET', `/${mod}`, app.token)
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setCboCash(result.cash);
          setActiveStep(1);
          setModalAdd(true);
          setVerify(false);
          setAttr(initAttr);
          setBlockScan(true);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          alert(result.msg);
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const auditAuthen = () => {
    if (!checkVerify()) return;
    setLoading(true);
    setStepLoading(true);
    FetchData('POST', `/${mod}`, app.token, {
      username: attr.username,
      password: attr.password,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAudit(result.audit);
          setAttr(initAttr);
          setVerify(false);
          setActiveStep(activeStep + 1);
          setList([]);
          setDrop(0);
          setTotal('');
          printRef.current.print();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          if (result.msg === 'role_not_audit') {
            showNotify('User ไม่ใช่ผู้ตรวจสอบ', 'error');
            return;
          }
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
        setStepLoading(false);
      });
  };

  const counting = () => {
    if (list.filter((item) => item.quantity > 0).length === 0) {
      showNotify('กรุณานับเงิน', 'error');
      return;
    }
    setLoading(true);
    setStepLoading(true);
    FetchData('OPTIONS', `/${mod}/counting`, app.token, {
      list: list.filter((item) => item.quantity > 0),
      audit: audit,
      client: app.client,
      drop: drop,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          printRef.current.print(result);
          setActiveStep(activeStep + 1);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
        setStepLoading(false);
      });
  };

  const editTotalCash = (row) => {
    const data = [];
    let result = 0;

    list.map((item) => (row.id === item.id ? null : data.push(item)));

    data.push(row);
    setList(data);

    data.forEach(function (item) {
      result += item.total * 1;
    });

    setTotal(result.toString());
  };

  const close = () => {
    setModalAdd(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const handleNext = () => {
    if (activeStep === 1) {
      auditAuthen();
    } else if (activeStep === 2) {
      counting();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const renderStepper = () => {
    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={initStepper}>
            <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
          </StepLabel>
        </Step>
      );
    };

    return (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {stepLevel('authen')}
          {stepLevel('counting')}
          {stepLevel('success')}
        </Stepper>
      </MDBox>
    );
  };

  const renderAuthen = () => {
    return (
      <Card sx={{ backgroundColor: '#F5F5F5' }}>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox mt={1} mb={5} textAlign='center'>
            <MDTypography variant='h3' fontWeight='medium' color='dark'>
              AUDIT AUTHENTICATION
            </MDTypography>
          </MDBox>
          <MDBox
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();
              auditAuthen();
            }}
          >
            <MDBox mb={2}>
              <MDInput
                name='user_name'
                type='text'
                fullWidth
                onChange={(e) => setAttr({ ...attr, username: e.target.value })}
                sx={{ input: { color: '#000000', fontSize: '15px' } }}
                value={attr.username}
                InputProps={{
                  maxLength: 50,
                  autoFocus: true,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <MDTypography variant='body' color='dark' sx={{ width: 65 }}>
                        {t('authen.username')} :
                      </MDTypography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <MDTypography variant='body' color='dark'>
                        <Icon>person</Icon>
                      </MDTypography>
                    </InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
              {verify ? (
                <MDTypography color='error' variant='caption'>
                  <Icon>priority_high</Icon> {t(`authen.user_incorrect`)}
                </MDTypography>
              ) : null}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                name='user_pass'
                type='password'
                fullWidth
                onChange={(e) => setAttr({ ...attr, password: e.target.value })}
                sx={{ input: { color: '#000000', fontSize: '15px' } }}
                value={attr.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <MDTypography variant='body' color='dark' sx={{ width: 65 }}>
                        {t('authen.password')} :
                      </MDTypography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <MDTypography variant='body' color='dark'>
                        <Icon>lock_open</Icon>
                      </MDTypography>
                    </InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton type='submit' variant='contained' color='info' fullWidth disabled={loading}>
                <MDTypography variant='h5' color='white'>
                  {t('authen.signin')} <Icon>login</Icon>
                </MDTypography>
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  const renderCount = () => {
    return (
      <MDBox mt={1}>
        <MDBox>
          <MDBox>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <MDInput
                  type='number'
                  fullWidth
                  onChange={(e) => setDrop(e.target.value)}
                  value={drop}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <span>เงินดรอป :</span>
                      </InputAdornment>
                    ),
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <MDBox className='card'>
                  <TableContainer sx={{ boxShadow: 0 }}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <TableCell align='right' width={40}>
                            &nbsp;
                          </TableCell>
                          <TableCell align='right' width={90}>
                            เงินสด
                          </TableCell>
                          <TableCell align='center'>จำนวน</TableCell>
                          <TableCell align='right' width={110}>
                            รวม
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cboCash
                          .filter((row) => row.id % 2 !== 0)
                          .map((row) => (
                            <CashInfo parent={row} callback={editTotalCash} key={row.id} />
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <MDBox className='card'>
                  <TableContainer sx={{ boxShadow: 0 }}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <TableCell align='right' width={40}>
                            &nbsp;
                          </TableCell>
                          <TableCell align='right' width={90}>
                            เงินสด
                          </TableCell>
                          <TableCell align='center'>จำนวน</TableCell>
                          <TableCell align='right' width={110}>
                            รวม
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cboCash
                          .filter((row) => row.id % 2 === 0)
                          .map((row) => (
                            <CashInfo parent={row} callback={editTotalCash} key={row.id} />
                          ))}
                        <TableRow>
                          <TableCell align='right' colSpan={2}>
                            <MDTypography variant='h4'>รวม</MDTypography>
                          </TableCell>
                          <TableCell align='right' className='color-danger' colSpan={2}>
                            <MDTypography variant='h4'>{Func.numberDisplay(total, 2)}</MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  const renderSuccess = () => {
    return (
      <MDBox mt={3}>
        <Grid container spacing={1}>
          <Grid item xs={12} align='center'>
            <Alert severity='success'>บันทึกข้อมูลเรียบร้อยแล้ว</Alert>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderStep = () => {
    if (activeStep === 1) return renderAuthen();
    if (activeStep === 2) return renderCount();
    if (activeStep === 3) return renderSuccess();

    return null;
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          <i className='fad fa-clipboard-list color-danger' /> &nbsp; สำรวจแคชเชียร์
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {renderStepper()}
              </Grid>
              <Grid item xs={12}>
                <MDBox height={420}>{renderStep()}</MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              {activeStep > 2 ? null : (
                <MDButton
                  variant='text'
                  color='dark'
                  disabled={activeStep < 2}
                  onClick={handleBack}
                >
                  <Icon>chevron_left</Icon>&nbsp;ย้อนกลับ
                </MDButton>
              )}
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              {activeStep === 3 ? (
                <MDButton variant='text' color='dark' onClick={close} disabled={stepLoading}>
                  {stepLoading ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : (
                    <Icon>close</Icon>
                  )}
                  ปิด &nbsp;
                </MDButton>
              ) : (
                <MDButton
                  variant='text'
                  color='info'
                  disabled={activeStep > 2 || stepLoading}
                  onClick={handleNext}
                >
                  ถัดไป &nbsp;
                  {stepLoading ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : (
                    <Icon>chevron_right</Icon>
                  )}
                </MDButton>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <MDBox>
      <Tooltip title='Drop money'>
        <MDButton
          variant='contained'
          color='dark'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={() => {
            init();
          }}
          ref={btnRef}
          disabled={loading || buttonLoading}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-clipboard-list' />
          )}
          &nbsp; สำรวจแคชเชียร์
        </MDButton>
      </Tooltip>
      {notify}

      {renderAdd()}
      <PosPrintSurvey ref={printRef} />
    </MDBox>
  );
}

PosSurvey.propTypes = {
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};
